import { OverlayModule } from '@angular/cdk/overlay';
import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  forwardRef,
  Input,
  type OnChanges,
  type OnInit,
  type SimpleChanges,
  ViewChild,
} from '@angular/core';
import {
  type ControlValueAccessor,
  FormControl,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import type { AccessManagementOrgUnitCollectionRecord } from '@cdw-ae/common-data';
import { CdwButtonModule } from '@cdw-ae/common-ui-components-button';
import { CdwCheckboxModule } from '@cdw-ae/common-ui-components-checkbox';
import { CdwRadioButtonModule } from '@cdw-ae/common-ui-components-radio-button';
import { CommonUiComponentsTextInputModule } from '@cdw-ae/common-ui-components-text-input';
import {
  CdwTreeModule,
  type TreeNode,
} from '@cdw-ae/common-ui-components-tree';
import { debounceTime, Subject } from 'rxjs';

export interface OuCollectionPickerValue {
  ous: string[];
  collectionIds: number[];
}

@Component({
  selector: 'lib-cdw-ou-collection-picker',
  templateUrl: './cdw-ou-collection-picker.component.html',
  styleUrls: ['./cdw-ou-collection-picker.component.scss'],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    OverlayModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    CdwButtonModule,
    CdwCheckboxModule,
    CdwRadioButtonModule,
    CdwTreeModule,
    CommonUiComponentsTextInputModule,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CdwOuCollectionPickerComponent),
      multi: true,
    },
  ],
})
export class CdwOuCollectionPickerComponent
  implements OnInit, OnChanges, ControlValueAccessor
{
  @ViewChild('triggerOrigin', { read: ElementRef })
  private triggerEl?: ElementRef;

  @Input() ouTreeData: TreeNode[] = [];
  @Input() ouCollections: AccessManagementOrgUnitCollectionRecord[] = [];
  @Input() height = '500px';
  @Input() multiSelect = true;
  @Input() progressiveParentSelect = true;
  @Input() placeholder = 'Select OUs or OU collections...';

  isOpen = false;
  ouSectionExpanded = true;
  collectionsSectionExpanded = true;
  treeControl = new FormControl<string[] | string | null>(null);

  searchText = '';
  filterValue = '';
  filteredCollections: AccessManagementOrgUnitCollectionRecord[] = [];
  selectedCollectionIds = new Set<number>();
  selectedOus: string[] = [];
  renderedTreeNodeCount = 0;

  // Snapshot for working state
  private snapshotOus: string[] = [];
  private snapshotCollectionIds = new Set<number>();

  inputChanged = new Subject<string>();

  onChange = (_value: OuCollectionPickerValue) => {
    // ControlValueAccessor placeholder
  };

  onTouched = () => {
    // ControlValueAccessor placeholder
  };

  get dynamicTreeHeight(): string {
    const nodeCount = this.renderedTreeNodeCount || 0;
    const itemHeight = 40;
    const calculatedHeight = nodeCount * itemHeight + 5;
    return `${calculatedHeight}px`;
  }

  get selectionDisplayText(): string {
    const ouCount = this.selectedOus.length;
    const collectionCount = this.selectedCollectionIds.size;

    // Single-select mode: show the actual name
    if (!this.multiSelect) {
      // Single OU collection selected
      if (collectionCount === 1) {
        const collectionId = Array.from(this.selectedCollectionIds)[0];
        const collection = this.ouCollections.find(
          (c) => c.id === collectionId
        );
        return collection ? `${collection.name} (OU Collection)` : '';
      }

      // Single OU selected
      if (ouCount === 1) {
        const ouId = this.selectedOus[0];
        const ouName = this.findOuNameById(ouId, this.ouTreeData);
        return ouName || '';
      }

      return '';
    }

    // Multi-select mode: show counts
    const parts = [];
    if (ouCount > 0) {
      parts.push(`${ouCount} OU${ouCount > 1 ? 's' : ''}`);
    }
    if (collectionCount > 0) {
      parts.push(
        `${collectionCount} OU collection${collectionCount > 1 ? 's' : ''}`
      );
    }

    return parts.length > 0 ? `${parts.join(' and ')} selected` : '';
  }

  private findOuNameById(ouId: string, nodes: TreeNode[]): string | null {
    for (const node of nodes) {
      if (node.value === ouId) {
        return node.displayName;
      }
      if (node.children) {
        const found = this.findOuNameById(ouId, node.children);
        if (found) return found;
      }
    }
    return null;
  }

  dropdownWidth = 0;

  ngOnInit(): void {
    this.filteredCollections = [...this.ouCollections];

    this.inputChanged.pipe(debounceTime(300)).subscribe((inputValue) => {
      this.filterValue = inputValue;
      this.filterCollections(inputValue);
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['ouCollections']?.currentValue) {
      this.filteredCollections = this.filterCollectionsList(
        changes['ouCollections'].currentValue,
        this.filterValue
      );
    }
  }

  registerOnChange(fn: (value: OuCollectionPickerValue) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  writeValue(value: OuCollectionPickerValue | null): void {
    if (!value) {
      this.selectedOus = [];
      this.selectedCollectionIds.clear();
      this.snapshotOus = [];
      this.snapshotCollectionIds = new Set();
      return;
    }

    this.selectedOus = value.ous || [];
    this.selectedCollectionIds = new Set(value.collectionIds || []);
    this.snapshotOus = [...this.selectedOus];
    this.snapshotCollectionIds = new Set(this.selectedCollectionIds);
  }

  openDropdown(): void {
    if (this.isOpen) return;
    // Capture trigger width for the panel
    this.dropdownWidth = this.triggerEl?.nativeElement?.offsetWidth ?? 0;

    // Restore tree selection state before overlay creates a new tree instance
    // In single-select mode, pass the first element as a string; in multi-select mode, pass the array
    if (this.multiSelect) {
      this.treeControl.setValue(this.snapshotOus);
    } else {
      // In single-select mode, pass a single string value (tree component expects this)
      const singleValue =
        this.snapshotOus.length > 0 ? this.snapshotOus[0] : null;
      this.treeControl.setValue(singleValue);
    }

    // Clear filterValue before opening so the tree's ngOnInit sets up unfiltered,
    // then restore it in a setTimeout so ngOnChanges sees it as a change and applies the filter.
    const savedFilter = this.filterValue;
    this.filterValue = '';
    this.isOpen = true;

    if (savedFilter) {
      setTimeout(() => {
        this.filterValue = savedFilter;
      });
    }
  }

  applySelection(): void {
    this.selectedOus = [...this.snapshotOus];
    this.selectedCollectionIds = new Set(this.snapshotCollectionIds);
    this.emitValue();
    this.isOpen = false;
  }

  cancelSelection(): void {
    this.isOpen = false;
  }

  toggleOuSection(): void {
    this.ouSectionExpanded = !this.ouSectionExpanded;
  }

  toggleCollectionsSection(): void {
    this.collectionsSectionExpanded = !this.collectionsSectionExpanded;
  }

  onInputChange(event?: Event): void {
    const rawValue = (event?.target as HTMLInputElement)?.value || '';
    this.searchText = rawValue;
    this.inputChanged.next(rawValue.toLowerCase());
  }

  onRenderedNodeCountChanged(count: number): void {
    this.renderedTreeNodeCount = count;
  }

  onOuSelectionChanged(value: string | string[]): void {
    if (Array.isArray(value)) {
      this.snapshotOus = value;
    } else {
      this.snapshotOus = value ? [value] : [];
    }

    // In single-select mode, clear OU collections when an OU is selected
    if (!this.multiSelect && this.snapshotOus.length > 0) {
      this.snapshotCollectionIds = new Set();
    }
  }

  onCollectionCheckboxChange(event: unknown): void {
    const typedEvent = event as {
      checkboxValue: AccessManagementOrgUnitCollectionRecord;
      checked: boolean;
    };

    if (typedEvent.checked) {
      this.snapshotCollectionIds.add(typedEvent.checkboxValue.id);
    } else {
      this.snapshotCollectionIds.delete(typedEvent.checkboxValue.id);
    }
  }

  onCollectionRadioChange(
    collection: AccessManagementOrgUnitCollectionRecord
  ): void {
    // Clear all selections and select only this collection
    this.snapshotCollectionIds = new Set([collection.id]);
    // Also clear OU selections since only one entity can be selected
    this.snapshotOus = [];
    this.treeControl.setValue([]);
  }

  isCollectionSelected(
    collection: AccessManagementOrgUnitCollectionRecord
  ): boolean {
    return this.snapshotCollectionIds.has(collection.id);
  }

  private filterCollections(searchText: string): void {
    this.filteredCollections = this.filterCollectionsList(
      this.ouCollections,
      searchText
    );
  }

  private filterCollectionsList(
    collections: AccessManagementOrgUnitCollectionRecord[],
    searchText: string
  ): AccessManagementOrgUnitCollectionRecord[] {
    if (!searchText) {
      return [...collections];
    }
    return collections.filter((c) =>
      c.name.toLowerCase().includes(searchText.toLowerCase())
    );
  }

  private emitValue(): void {
    const value: OuCollectionPickerValue = {
      ous: [...this.selectedOus],
      collectionIds: Array.from(this.selectedCollectionIds),
    };
    this.onChange(value);
    this.onTouched();
  }
}
